<mat-card class="rounded-3xl border border-slate-200 p-4 shadow-lg shadow-slate-200/60">
  <div class="mb-3 flex items-center justify-between gap-3">
    <h3 class="text-lg font-semibold text-slate-900">
      Sessions
      @if (activeCount() > 0) {
        <span class="ml-2 text-sm font-normal text-slate-500">{{ activeCount() }} scheduled or held</span>
      }
    </h3>
    @if (canRecord() && !formOpen()) {
      <button mat-flat-button type="button" [disabled]="!hasServiceConsent()" (click)="openNew()">Schedule session</button>
    }
  </div>

  @if (canRecord() && !hasServiceConsent()) {
    <p class="mb-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
      Service consent must be recorded on the Consent tab before a session can be scheduled.
    </p>
  }

  @if (formOpen()) {
    <form [formGroup]="form" (ngSubmit)="save()" class="mb-4 grid gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <h4 class="font-semibold text-slate-900">{{ editingSessionId() ? 'Edit session' : 'Schedule a session' }}</h4>
      <div class="grid gap-4 md:grid-cols-2">
        <date-time-picker label="Date and time *" formControlName="sessionDate" />
        <mat-form-field appearance="outline">
          <mat-label>Duration (minutes)</mat-label>
          <input matInput type="number" min="1" max="600" formControlName="durationMinutes" />
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Medium</mat-label>
          <mat-select formControlName="counsellingMediumId">
            <mat-option value="">Not specified</mat-option>
            @for (item of mediums(); track item.id) {
              <mat-option [value]="item.id">{{ item.name ?? item.code }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Location or platform</mat-label>
          <input matInput maxlength="200" formControlName="location" />
        </mat-form-field>
        <mat-form-field appearance="outline" class="md:col-span-2">
          <mat-label>Notes</mat-label>
          <textarea matInput rows="2" formControlName="notes"></textarea>
        </mat-form-field>
      </div>
      <div class="flex justify-end gap-2">
        <button mat-stroked-button type="button" (click)="closeForm()">Close</button>
        <button mat-flat-button type="submit" [disabled]="form.invalid || interventionStore.savingSession()">
          @if (interventionStore.savingSession()) {
            <mat-spinner diameter="20" class="mr-2" />
          }
          {{ editingSessionId() ? 'Save changes' : 'Schedule' }}
        </button>
      </div>
    </form>
  }

  @if (interventionStore.sessionsLoading() && interventionStore.sessions().length === 0) {
    <div class="flex items-center gap-3 text-sm text-slate-600">
      <mat-spinner diameter="20" />
      Loading sessions...
    </div>
  } @else if (interventionStore.sessions().length === 0) {
    <p class="text-sm text-slate-500">No sessions have been scheduled yet.</p>
  } @else {
    <div class="grid gap-2">
      @for (session of interventionStore.sessions(); track session.id) {
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 py-2 text-sm last:border-b-0">
          <div class="grid gap-0.5" [class.opacity-60]="session.status === 'Cancelled'">
            <div class="flex items-center gap-2">
              <span class="font-medium text-slate-900" [class.line-through]="session.status === 'Cancelled'">
                Session {{ session.sessionNumber }} · {{ session.sessionDate | date: 'yyyy-MM-dd HH:mm' }}
              </span>
              <span
                class="rounded-full px-2 py-0.5 text-xs font-medium"
                [class.bg-blue-50]="session.status === 'Scheduled'"
                [class.text-blue-700]="session.status === 'Scheduled'"
                [class.bg-green-50]="session.status === 'Attended'"
                [class.text-green-700]="session.status === 'Attended'"
                [class.bg-amber-50]="session.status === 'NoShow'"
                [class.text-amber-700]="session.status === 'NoShow'"
                [class.bg-slate-100]="session.status === 'Cancelled'"
                [class.text-slate-500]="session.status === 'Cancelled'">
                {{ statusLabel(session.status) }}
              </span>
            </div>
            <span class="text-slate-600">
              {{ session.affiliateName ?? 'Internal' }}
              @if (session.counsellingMediumName) {
                · {{ session.counsellingMediumName }}
              }
              @if (session.durationMinutes) {
                · {{ session.durationMinutes }} min
              }
              @if (session.location) {
                · {{ session.location }}
              }
            </span>
            @if (session.status === 'Attended' && (session.checkInTime || session.checkOutTime)) {
              <span class="text-slate-600">
                In {{ session.checkInTime ? (session.checkInTime | date: 'HH:mm') : '-' }} · Out
                {{ session.checkOutTime ? (session.checkOutTime | date: 'HH:mm') : '-' }}
              </span>
            }
            @if (session.status === 'NoShow' && session.noShowReason) {
              <span class="text-amber-700">Did not attend: {{ session.noShowReason }}</span>
            }
            @if (session.notes) {
              <span class="text-slate-500">{{ session.notes }}</span>
            }
          </div>

          <div class="flex flex-wrap items-center gap-2">
            @if (session.attendanceVerifiedDate) {
              <span class="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">Verified</span>
            }
            @if (usesSessionNotes() && session.status === 'Attended' && (canRecord() || hasNote(session.id))) {
              <a mat-stroked-button [routerLink]="['/interventions', interventionId(), 'sessions', session.id, 'notes']">
                {{ hasNote(session.id) ? 'View notes' : 'Add notes' }}
              </a>
            }
            @if (canRecord()) {
              @if (session.status === 'Scheduled') {
                <button mat-flat-button type="button" (click)="openAttendance(session)">Record attendance</button>
                <button mat-stroked-button type="button" (click)="openEdit(session)">Edit</button>
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="interventionStore.savingSession()"
                  (click)="cancel(session)">
                  Cancel session
                </button>
              } @else if ((session.status === 'Attended' || session.status === 'NoShow') && !session.attendanceVerifiedDate) {
                <button mat-stroked-button type="button" (click)="openAttendance(session)">Correct attendance</button>
              }
            }
            @if (canVerify() && (session.status === 'Attended' || session.status === 'NoShow') && !session.attendanceVerifiedDate) {
              <button
                mat-stroked-button
                type="button"
                [disabled]="interventionStore.savingSession()"
                (click)="verify(session)">
                Verify attendance
              </button>
            }
          </div>

          @if (attendanceSessionId() === session.id) {
            <form
              [formGroup]="attendanceForm"
              (ngSubmit)="saveAttendance()"
              class="grid w-full gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <mat-radio-group formControlName="attended" class="flex gap-4">
                <mat-radio-button [value]="true">Attended</mat-radio-button>
                <mat-radio-button [value]="false">Did not attend</mat-radio-button>
              </mat-radio-group>

              @if (attendanceForm.controls.attended.value) {
                <div class="grid gap-3 md:grid-cols-2">
                  <date-time-picker label="Check-in" formControlName="checkInTime" />
                  <date-time-picker label="Check-out" formControlName="checkOutTime" />
                </div>
              } @else {
                <mat-form-field appearance="outline">
                  <mat-label>Reason *</mat-label>
                  <textarea matInput rows="2" maxlength="500" formControlName="noShowReason"></textarea>
                </mat-form-field>
              }

              <div class="flex justify-end gap-2">
                <button mat-stroked-button type="button" (click)="closeAttendance()">Close</button>
                <button mat-flat-button type="submit" [disabled]="interventionStore.savingSession()">Save attendance</button>
              </div>
            </form>
          }
        </div>
      }
    </div>
  }
</mat-card>
